import type {
  ExperienceBlock,
  ExperiencePage,
  SessionConversationMessage,
  SessionEvent,
  SessionProtocolState,
  SessionView,
} from '@matter_extractor/shared';
import { assertNever, conversationItemsOf, isConversationPage } from '@matter_extractor/shared';

/**
 * The THREAD of the protocol page (§9.5), as a pure state machine.
 *
 * The screen draws bubbles; this module decides which bubbles exist. It is
 * written before the component and tested without a DOM, for the reason
 * `chat-transcript.ts` gives next door: the hard part of a live conversation is
 * not the markup, it is what a frame does to a thread a REST read has already
 * half-written - and that is exactly what a render test cannot pin down without
 * also asserting on layout.
 *
 * ## Two writers, one floor
 *
 * « Le client relit la session par REST à chaque connexion et le canal ne porte
 * que des deltas » (§9.5). So the REST view is the FLOOR of the thread and the
 * channel adds a TAIL on top of it:
 *
 *   thread = conversationThread(view.conversation ?? [], live)
 *
 * The tail holds what the channel has produced since the last connection: the
 * person's own bubble, posted the moment they press send, and the interviewer's
 * answer as it is written. A reconnection empties the tail and the re-read
 * refills the floor, which is what makes « une coupure du canal se rattrape par
 * la relecture, jamais par un doublon de bulle » true by construction rather
 * than by comparing texts.
 *
 * The one overlap left is worth naming: a `status` frame makes the route
 * re-read, and the floor then holds bubbles the tail still carries. Both
 * statuses that travel end the conversation - `closed` puts the ending of §5.3
 * over the whole window, and `finished` is an advance off this page, or, since
 * the skip of the noyau (noyau.md §4.5, N27), the end of the session ON this
 * page. That last case keeps the thread on screen, so a finished session reads
 * its floor ALONE (`conversationThread`'s third argument): the view that ended
 * it carries the whole thread, and nothing more can come on the channel.
 * Nothing else re-reads mid-conversation: `refetchOnWindowFocus` is off for the
 * whole app (`query-client.ts`).
 *
 * ## No progression, ever
 *
 * « Aucun indicateur de progression : ni numéro d'étape, ni compteur de tours,
 * ni statut d'objectif, ni minuteur » (§9.5). `SessionProtocolState` is the one
 * thing the person is told, and it says whether they may still speak - not
 * where they are. Nothing else of a turn crosses into this module, which is why
 * it reads `SessionEvent` and never `SessionTurn`.
 */

/**
 * ## The pages of the thread (prolongation.md §5, issue #186)
 *
 * The conversation stays mounted from the protocol page to the last page played
 * in it. On such a page the person never types: the interviewer's bubble is
 * written like any message, the ELEMENTS of the page arrive under it once it is
 * complete, and what the person clicks becomes THEIR bubble. Three more things
 * live here, all of them states and not pixels:
 *
 * - a bubble carries what it IS (`kind`) and the page it belongs to (`pageId`),
 *   so the elements can be hung under it and a bubble the REST floor already
 *   holds is never drawn twice;
 * - the person's bubble is NEVER posted optimistically: the advance answers
 *   with the view, whose conversation holds the click (`kind: 'click'`), so the
 *   floor is the only writer of it - no second truth to reconcile;
 * - `conversationEntries` lays the elements of each page among the bubbles: the
 *   current page's, live, under its bubble; a past page's, only what stays once
 *   it is answered (the portrait, a link, a copyable text).
 */

/** What a bubble IS: a message of the protocol, the interviewer's bubble of a page, or a click. */
export type ConversationBubbleKind = 'turn' | 'bubble' | 'click';

/** One bubble on screen: who said it, what it says, whether it is still being written. */
export type ConversationBubble = {
  /** Stable across renders - a turn id, a stamp, or the rank of a re-read message. */
  readonly key: string;
  readonly role: 'interviewer' | 'person';
  readonly text: string;
  /** True while the deltas of §9.5 are still landing in it. */
  readonly writing: boolean;
  /** Absent means `turn`: every bubble of the protocol conversation. */
  readonly kind?: ConversationBubbleKind;
  /** The page of the thread a `bubble` or a `click` belongs to. */
  readonly pageId?: string;
  /**
   * The click was a CARD taken: its text is quoted and cut to four lines
   * (prolongation.md §5), where the label of a Bouton is a plain bubble.
   */
  readonly quoted?: boolean;
};

/** What the CHANNEL holds, on top of the REST floor. */
export type ConversationLive = {
  /** The bubbles the channel has added since the last connection. */
  readonly tail: readonly ConversationBubble[];
  /** « Un indicateur de frappe entre l'envoi et le premier mot » (§9.5). */
  readonly typing: boolean;
  /** The bubble the deltas are landing in, by turn id, or null. */
  readonly streamingId: string | null;
  /** Where the page stands - the only progression the person is told about. */
  readonly state: SessionProtocolState;
  /** A turn of ours is in flight: the composer is inert until it lands. */
  readonly sending: boolean;
  /**
   * The pages whose bubble the channel completed (`bubble` frame), an EMPTY one
   * included: a failed call draws no bubble and the elements of the page come
   * alone (prolongation.md §4.6).
   */
  readonly bubbled: readonly string[];
};

/**
 * A page entered, or a channel just connected: nothing of our own on top of the
 * floor, and the conversation open.
 */
export const IDLE_CONVERSATION: ConversationLive = {
  tail: [],
  typing: false,
  streamingId: null,
  state: 'step',
  sending: false,
  bubbled: [],
};

export type ConversationAction =
  /** The channel (re)connected: the tail is dropped and the re-read refills the floor. */
  | { readonly kind: 'reconnected' }
  /**
   * The re-read a page of the thread asked for has landed: the messages of the
   * protocol the tail still carries are in the floor now (see `conversationLive`).
   */
  | { readonly kind: 'caught-up' }
  /** The person pressed send. `at` is there to key the bubble, and for nothing else. */
  | { readonly kind: 'sent'; readonly text: string; readonly at: string }
  /** One frame of `GET /api/s/{token}/events`, already parsed. */
  | { readonly kind: 'frame'; readonly event: SessionEvent }
  /** The POST was refused, worded by the caller from this very `code`. */
  | { readonly kind: 'refused'; readonly code: string | null };

/**
 * The thread as the screen draws it: the floor, then the tail.
 *
 * The re-read messages are keyed by RANK. They carry no identifier - a stored
 * turn draws its own and the contract deliberately does not publish it
 * (`sessionConversationMessageSchema`) - and a rank is stable for as long as
 * the array is, which is exactly as long as the element needs to be.
 *
 * `finished` drops the tail: a session that ended on this page is read from its
 * view alone (see the head, N27).
 */
export function conversationThread(
  messages: readonly SessionConversationMessage[],
  live: ConversationLive,
  finished = false,
): readonly ConversationBubble[] {
  const floor = messages.map(
    (message, rank): ConversationBubble => ({
      key: `relu-${rank}`,
      role: message.role,
      text: message.text,
      writing: false,
      ...(message.kind === 'turn' ? {} : { kind: message.kind }),
      ...(message.pageId === null ? {} : { pageId: message.pageId }),
    }),
  );
  return [...floor, ...(finished ? [] : live.tail.filter((bubble) => !alreadyRead(bubble, floor)))];
}

/**
 * A bubble of the tail the floor already holds - the overlap a re-read leaves
 * (the view is re-read when the session moves under the tab, and the tail is
 * not emptied by it).
 *
 * A bubble of a PAGE is identified by its page - a page of the thread is
 * visited once, and its bubble is written once (prolongation.md §4.2). The
 * goodbye served after the last click has no such handle: in a session that has
 * played pages in the thread, it is the LAST message of the floor and says the
 * same words. (Outside the thread nothing re-reads while the conversation is
 * drawn, and a repeated sentence of the protocol is a repeated sentence.)
 */
function alreadyRead(bubble: ConversationBubble, floor: readonly ConversationBubble[]): boolean {
  if (bubble.writing) return false;
  if (bubble.kind === 'bubble' && bubble.pageId !== undefined) {
    return floor.some((read) => read.kind === 'bubble' && read.pageId === bubble.pageId);
  }
  const last = floor[floor.length - 1];
  const inThread = floor.some((read) => read.kind === 'bubble' || read.kind === 'click');
  return (
    inThread &&
    bubble.role === 'interviewer' &&
    last !== undefined &&
    last.role === 'interviewer' &&
    last.kind === undefined &&
    last.text === bubble.text
  );
}

export function conversationLive(
  state: ConversationLive,
  action: ConversationAction,
): ConversationLive {
  switch (action.kind) {
    case 'reconnected':
      /*
        The catch-up. `state` is KEPT rather than reset to `step`: a protocol
        that closed itself is still closed after a dropped connection, and a
        composer coming back on a finished conversation would be an invitation
        the server can only refuse.
      */
      return { ...state, tail: [], typing: false, streamingId: null, sending: false };

    case 'caught-up':
      /*
        The session moved onto a page of the thread under this tab, and the
        route re-read it: the floor holds EVERY message of the protocol - the
        interviewer's, and the person's own bubble posted at the press - which
        the tail still carries, and they would be drawn a second time, under
        the page they led to. A message of the protocol is always complete
        before the first page is entered, and none is written after it until
        the goodbye, which a re-read of this kind never overtakes: so every
        settled plain message goes, and what is being written, and the bubbles
        and clicks of the pages (keyed by their page, `alreadyRead`), stay.
      */
      return {
        ...state,
        tail: state.tail.filter((bubble) => bubble.writing || bubble.kind !== undefined),
      };

    case 'sent':
      return {
        ...state,
        tail: [
          ...state.tail,
          { key: `envoi-${action.at}`, role: 'person', text: action.text, writing: false },
        ],
        // The dots go up on the PRESS and not on the `typing` frame: the frame
        // is a round trip away, and a composer that empties into silence reads
        // as a message that was lost.
        typing: true,
        sending: true,
      };

    case 'refused':
      /*
        Nothing was written, so the optimistic bubble is taken back: the person
        gets their words returned to the composer rather than a bubble that
        never reached anybody.

        `session-over` is the one refusal that also MOVES the page. The message
        route answers it when no question is waiting - the protocol closed
        itself while this tab was away, which is exactly what a reprise (§5.3)
        walks into, since the REST view carries the bubbles and not the state.
        Turning it into `closed` puts « Continuer » under the thread instead of
        leaving a composer that can only be refused again.

        It moves a page that is still on `step`, and no other: a refusal that
        lands during the goodbye (`farewell`, issue #163) must not bring
        « Continuer » in the middle of the reading - the `closed` frame at the
        end of the stream does that.
      */
      return {
        ...state,
        tail: withoutPendingBubble(state.tail),
        typing: false,
        sending: false,
        state: action.code === 'session-over' && state.state === 'step' ? 'closed' : state.state,
      };

    case 'frame':
      return applyFrame(state, action.event);
  }
}

/* --------------------------------------------------------------- the frames */

function applyFrame(state: ConversationLive, event: SessionEvent): ConversationLive {
  switch (event.type) {
    case 'typing':
      return { ...state, typing: true };

    case 'delta':
      return withDelta(state, event.turnId, event.text);

    case 'message':
      return settled(state, event.turn.id, event.turn.message);

    case 'protocol':
      return withProtocolState(state, event.state);

    case 'status':
      // A stored status, whose consequence is a REST view and not a bubble.
      // The route re-reads; nothing of the thread moves here.
      return state;

    case 'noyau':
      // The determination is settled (noyau.md §4.6): its consequence is an
      // advance to REPLAY, never a bubble - nothing of the thread moves here.
      // The route replays it, by the machine of `noyau-wait.ts`, which holds
      // the advance the thread knows nothing about.
      return state;

    case 'bubble':
      return bubbleSettled(state, event.bubble);
  }
}

/**
 * Where the page stands, as a `protocol` frame says it. Total over the five
 * states, so a sixth one fails the build here rather than falling into a branch
 * that happens to hide the composer.
 */
function withProtocolState(
  state: ConversationLive,
  protocol: SessionProtocolState,
): ConversationLive {
  switch (protocol) {
    case 'step':
      /*
        The page OPENING - the engine publishes it before serving the first
        message - so it must not touch a turn in flight.
      */
      return { ...state, state: 'step' };

    case 'farewell':
      /*
        The goodbye of a farewell step (issue #163, §9.5) is ABOUT to be
        written: the frame travels before its first delta. The composer goes
        with the state - the component offers it on `step` alone -, and the
        turn in flight is kept whole: the dots stay up until the first word,
        exactly as on any other answer, and a delta already landed keeps
        landing in its bubble. `sending` is released because nothing of the
        person's is pending any more; there is no composer for it to hold inert.
        « Continuer » is `closed`'s and never this one's: it must not come in
        the middle of the reading.
      */
      return { ...state, state: 'farewell', sending: false };

    case 'page':
      /*
        A page played in the conversation (prolongation.md §4.1): the composer
        goes with the state, exactly as on a goodbye, and the person only
        clicks. Published BEFORE the first delta of the page's bubble, so the
        input disappears at the first word.
      */
      return { ...state, state: 'page', sending: false };

    case 'closed':
    case 'experience-closed':
      /*
        The end of the conversation, and the only thing that releases the page
        when no message follows: a `close-protocol` turn publishes no `message`
        frame at all. A bubble still being written is FROZEN where it stands -
        the goodbye whose interviewer call failed mid-stream (§8.1) closes the
        protocol anyway, and « Continuer » comes under the last words shown
        rather than under a bubble that claims more is coming.
      */
      return {
        ...state,
        state: protocol,
        typing: false,
        streamingId: null,
        sending: false,
        tail: state.tail.map((bubble) => (bubble.writing ? { ...bubble, writing: false } : bubble)),
      };

    default:
      return assertNever(protocol, 'session protocol state');
  }
}

/** One piece of the interviewer's message, landing in the bubble it names. */
function withDelta(state: ConversationLive, turnId: string, text: string): ConversationLive {
  const last = state.tail[state.tail.length - 1];
  if (state.streamingId === turnId && last !== undefined) {
    return {
      ...state,
      typing: false,
      tail: [...state.tail.slice(0, -1), { ...last, text: last.text + text }],
    };
  }
  return {
    ...state,
    typing: false,
    streamingId: turnId,
    tail: [
      ...state.tail,
      {
        key: `tour-${turnId}`,
        role: 'interviewer',
        text,
        writing: true,
        // On a page of the thread the deltas write the BUBBLE of that page; its
        // page is known at the `bubble` frame, which settles it.
        ...(state.state === 'page' ? { kind: 'bubble' as const } : {}),
      },
    ],
  };
}

/**
 * The bubble of a page is complete (prolongation.md §4.2): the elements of the
 * page can come under it. The frame carries the WHOLE message and replaces the
 * streamed bubble, for the reason `settled` gives - and an EMPTY one draws
 * nothing, the page's elements coming alone (§4.6), but still says the bubble
 * is over.
 */
function bubbleSettled(
  state: ConversationLive,
  bubble: { readonly id: string; readonly pageId: string; readonly message: string },
): ConversationLive {
  const streaming = state.streamingId === bubble.id;
  const kept = streaming ? state.tail.slice(0, -1) : state.tail;
  return {
    ...state,
    typing: false,
    streamingId: null,
    bubbled: state.bubbled.includes(bubble.pageId)
      ? state.bubbled
      : [...state.bubbled, bubble.pageId],
    tail:
      bubble.message === ''
        ? kept
        : [
            ...kept,
            {
              key: `tour-${bubble.id}`,
              role: 'interviewer',
              text: bubble.message,
              writing: false,
              kind: 'bubble',
              pageId: bubble.pageId,
            },
          ],
  };
}

/**
 * The bubble is final (§9.5).
 *
 * The frame carries the WHOLE message, so the streamed bubble is replaced
 * rather than appended to: a delta lost on the way would otherwise leave a hole
 * in the middle of a sentence that nothing ever repairs.
 *
 * An EMPTY message is a call that failed (§8.7), and it draws no bubble - the
 * régime `conversationOf` applies server side, so a reload and a live tab show
 * the same thread.
 */
function settled(state: ConversationLive, turnId: string, text: string): ConversationLive {
  const streaming = state.streamingId === turnId;
  const kept = streaming ? state.tail.slice(0, -1) : state.tail;
  return {
    ...state,
    typing: false,
    streamingId: null,
    sending: false,
    tail:
      text === ''
        ? kept
        : [...kept, { key: `tour-${turnId}`, role: 'interviewer', text, writing: false }],
  };
}

/** Take back the bubble the person posted optimistically, and nothing else. */
function withoutPendingBubble(tail: readonly ConversationBubble[]): readonly ConversationBubble[] {
  const last = tail[tail.length - 1];
  return last !== undefined && last.role === 'person' ? tail.slice(0, -1) : tail;
}

/* ------------------------------------------------- the pages of the thread */

/**
 * Where the elements of a page hang in the thread: right under its bubble.
 *
 * - `live` - the page the person is on: everything it shows, clickable;
 * - `kept` - a page already answered: only what STAYS in the thread once its
 *   exit was clicked (the portrait, a link, a copyable text - prolongation.md
 *   §5). The cards, the Bouton and the fields left with the click, and what
 *   they said is now the person's bubble.
 */
export type ThreadSlot = {
  readonly entry: 'items';
  readonly key: string;
  readonly pageId: string;
  readonly mode: 'live' | 'kept';
};

/** What the thread draws, in order: a bubble, or the elements of a page. */
export type ThreadEntry = ConversationBubble | ThreadSlot;

export function isThreadSlot(entry: ThreadEntry): entry is ThreadSlot {
  return 'entry' in entry;
}

/** The page of the thread the session stands on, or null (a protocol page is not one). */
export function threadPageOf(
  pages: readonly ExperiencePage[],
  currentPageId: string | null,
): ExperiencePage | null {
  const page = pages.find((one) => one.id === currentPageId);
  return page !== undefined && isConversationPage(page) ? page : null;
}

/**
 * Where the page stands, from the channel and from what the REST view says.
 *
 * The channel says it (`protocol` frames) and a reload says nothing: the view
 * carries the bubbles and not the state, so a tab that opens on a page of the
 * thread - whose frames it has missed - reads the state off the thread:
 *
 * - the thread has begun and the last message is the interviewer's, a message
 *   of the protocol (`turn`) - the goodbye that follows the last click -: the
 *   protocol is over and « Continuer » is due;
 * - otherwise the page is being played.
 *
 * A state the channel already moved off `step` is never overridden.
 */
export function conversationStateOf(
  live: ConversationLive,
  thread: readonly ConversationBubble[],
  onThreadPage: boolean,
): SessionProtocolState {
  if (live.state !== 'step' || !onThreadPage) return live.state;
  const last = thread[thread.length - 1];
  const begun = thread.some((bubble) => bubble.kind === 'bubble' || bubble.kind === 'click');
  const goodbye = last !== undefined && last.role === 'interviewer' && last.kind === undefined;
  return begun && goodbye ? 'closed' : 'page';
}

/** What `conversationEntries` needs to know of the session, and nothing more. */
export type ThreadContext = {
  readonly pages: readonly ExperiencePage[];
  /** The page the session stands on. */
  readonly currentPageId: string | null;
  /** The EFFECTIVE state - `conversationStateOf`. */
  readonly state: SessionProtocolState;
  /** What the channel completed since the last connection. */
  readonly bubbled: readonly string[];
  /**
   * The wait for a bubble was given up: nothing has come for long enough to
   * say the call failed and the REST view left its empty bubble out. The
   * elements come alone - the person is never blocked (prolongation.md §4.6).
   */
  readonly gaveUp: boolean;
};

/**
 * The thread as it is drawn: the bubbles, and under each the elements of its
 * page.
 *
 * `withheld` says the CURRENT page's elements are not drawn yet because its
 * bubble is not complete - the one case where `gaveUp` is worth arming.
 */
export function conversationEntries(
  thread: readonly ConversationBubble[],
  context: ThreadContext,
): { readonly entries: readonly ThreadEntry[]; readonly withheld: boolean } {
  const threadPages = new Map(
    context.pages.filter(isConversationPage).map((page) => [page.id, page] as const),
  );
  const answered = new Set<string>();
  const bubbled = new Set<string>(context.bubbled);
  for (const bubble of thread) {
    if (bubble.pageId === undefined) continue;
    if (bubble.kind === 'click') answered.add(bubble.pageId);
    if (bubble.kind === 'bubble' && !bubble.writing) bubbled.add(bubble.pageId);
  }

  const current =
    context.currentPageId !== null && threadPages.has(context.currentPageId)
      ? context.currentPageId
      : null;
  const playing = context.state === 'page' && current !== null && !answered.has(current);

  const placed = new Set<string>();
  const slotFor = (pageId: string): ThreadSlot | null => {
    const page = threadPages.get(pageId);
    if (page === undefined || placed.has(pageId)) return null;
    const mode = playing && pageId === current ? 'live' : 'kept';
    if (mode === 'kept' && !keepsItems(page)) return null;
    placed.add(pageId);
    return { entry: 'items', key: `fil-${pageId}`, pageId, mode };
  };

  const entries: ThreadEntry[] = [];
  for (const bubble of thread) {
    if (bubble.kind === 'click' && bubble.pageId !== undefined) {
      // A page whose bubble failed has no bubble to hang its portrait under.
      const slot = slotFor(bubble.pageId);
      if (slot !== null) entries.push(slot);
      const page = threadPages.get(bubble.pageId);
      entries.push(page !== undefined && takesCard(page) ? { ...bubble, quoted: true } : bubble);
      continue;
    }
    entries.push(bubble);
    if (bubble.kind === 'bubble' && !bubble.writing && bubble.pageId !== undefined) {
      const slot = slotFor(bubble.pageId);
      if (slot !== null) entries.push(slot);
    }
  }

  let withheld = false;
  if (playing && current !== null && !placed.has(current)) {
    if (bubbled.has(current) || context.gaveUp) {
      const slot = slotFor(current);
      if (slot !== null) entries.push(slot);
    } else {
      withheld = true;
    }
  }
  return { entries, withheld };
}

/**
 * Whether a block of a page of the thread STAYS in it once the page is answered:
 * the portrait, a link, a copyable text - what is a thing to keep, where the
 * cards, the Bouton and the fields are things to answer.
 */
export function isKeptThreadItem(block: ExperienceBlock): boolean {
  switch (block.type) {
    case 'noyau':
      return block.mode === 'portrait';
    case 'link':
    case 'copyable':
      return true;
    default:
      return false;
  }
}

function keepsItems(page: ExperiencePage): boolean {
  return conversationItemsOf(page).some(isKeptThreadItem);
}

/** The page's way out is a card: its click reads as a quoted text. */
function takesCard(page: ExperiencePage): boolean {
  return conversationItemsOf(page).some(
    (block) => block.type === 'noyau' && block.mode !== 'portrait',
  );
}

/**
 * How long the interviewer's bubble of a page is waited for when nothing is
 * happening on the channel (no typing, no delta). Past it the page's elements
 * come alone: a call that failed leaves no bubble, and a view that omits an empty
 * bubble cannot say so (prolongation.md §4.6).
 */
export const BUBBLE_GRACE_MS = 6000;

/**
 * How long a bubble may stay incomplete, stream or no stream, before the elements
 * of its page come alone: past the interviewer seat's budget (60 s) the server
 * writes an empty bubble and says so, so a wait this long is a writing that
 * stopped with no frame - and the person is never kept (prolongation.md §4.6).
 */
export const BUBBLE_STALL_MS = 90_000;

/** The thread of a session as the screen draws it: entries, state, and whether a page is awaited. */
export type SessionThread = {
  readonly thread: readonly ThreadEntry[];
  /** The effective state of the page - `conversationStateOf`. */
  readonly state: SessionProtocolState;
  /** The current page's elements wait for a bubble that has not completed. */
  readonly withheld: boolean;
};

/**
 * The whole thread of a session view and the channel's tail: the bubbles (the
 * floor and the tail), the effective state, and the elements of the pages laid
 * among them. One function, so the route and the tests read it the same way.
 */
export function sessionThread(
  view: Pick<SessionView, 'conversation' | 'status' | 'currentPageId' | 'content'>,
  live: ConversationLive,
  gaveUp: boolean,
): SessionThread {
  const onThreadPage = threadPageOf(view.content.pages, view.currentPageId) !== null;
  // A session the skip of the noyau ended ON the protocol page (N27) is read
  // from its view alone; on a page of the thread the goodbye is served AFTER the
  // click, so its tail is kept - `conversationThread` drops what the view has.
  const bubbles = conversationThread(
    view.conversation ?? [],
    live,
    view.status === 'finished' && !onThreadPage,
  );
  const state = conversationStateOf(live, bubbles, onThreadPage);
  const { entries, withheld } = conversationEntries(bubbles, {
    pages: view.content.pages,
    currentPageId: view.currentPageId,
    state,
    bubbled: live.bubbled,
    gaveUp,
  });
  return { thread: entries, state, withheld };
}
